import { CONFIGURATION } from "@revolt/common";

import { State } from "..";

import { AbstractStore } from ".";

export type Session = {
  _id: string;
  token: string;
  userId: string;
  valid: boolean;
};

/**
 * PeyPhone: mirror the session into a long-lived, server-set HttpOnly cookie
 * (see frontend/nginx.conf, /_pp/session). Safari/ITP may wipe IndexedDB after
 * 7 days without interaction; server-set cookies from the same site are not
 * capped, so we restore the session from it when local storage is empty.
 */
const PP_SESSION_URL = "/_pp/session";

function b64urlEncode(text: string) {
  const bytes = new TextEncoder().encode(text);
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function b64urlDecode(text: string) {
  const b64 = text.replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64 + "===".slice((b64.length + 3) % 4));
  return new TextDecoder().decode(
    Uint8Array.from(bin, (c) => c.charCodeAt(0)),
  );
}

function cookieSync(method: "POST" | "DELETE", session?: Session) {
  try {
    const headers: Record<string, string> = { "X-PP-Req": "1" };
    if (session) {
      headers["X-PP-Session"] = b64urlEncode(
        JSON.stringify({
          _id: session._id,
          token: session.token,
          userId: session.userId,
        }),
      );
    }
    return fetch(PP_SESSION_URL, {
      method,
      headers,
      credentials: "same-origin",
      cache: "no-store",
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    return Promise.resolve(undefined);
  }
}

async function cookieLoad(): Promise<Session | undefined> {
  try {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 4000);
    const res = await fetch(PP_SESSION_URL, {
      headers: { "X-PP-Req": "1" },
      credentials: "same-origin",
      cache: "no-store",
      signal: ctrl.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return;
    const raw = (await res.text()).trim();
    if (!raw) return;
    const data = JSON.parse(b64urlDecode(raw));
    if (
      typeof data?._id === "string" &&
      typeof data?.token === "string" &&
      typeof data?.userId === "string"
    ) {
      return {
        _id: data._id,
        token: data.token,
        userId: data.userId,
        valid: true,
      };
    }
  } catch {
    // offline / not deployed / bad cookie: ignore
  }
}

export type TypeAuth = {
  /**
   * Session information
   */
  session?: Session;
};

/**
 * Authentication details store
 */
export class Auth extends AbstractStore<"auth", TypeAuth> {
  /**
   * Construct store
   * @param state State
   */
  constructor(state: State) {
    super(state, "auth");
  }

  /**
   * Hydrate external context
   */
  async hydrate(): Promise<void> {
    if (CONFIGURATION.DEVELOPMENT_TOKEN && CONFIGURATION.DEVELOPMENT_USER_ID) {
      this.setSession({
        _id: CONFIGURATION.DEVELOPMENT_SESSION_ID ?? "0",
        token: CONFIGURATION.DEVELOPMENT_TOKEN,
        userId: CONFIGURATION.DEVELOPMENT_USER_ID,
        valid: true,
      });
      return;
    }

    const session = this.get().session;
    if (session) {
      // refresh the rolling 1-year cookie (also migrates existing logins)
      void cookieSync("POST", session);
    } else {
      // local storage empty (e.g. Safari ITP purge): restore from cookie
      const restored = await cookieLoad();
      if (restored && !this.get().session) {
        this.set("session", restored);
      }
    }
  }

  /**
   * Generate default values
   */
  default(): TypeAuth {
    return {
      session: undefined,
    };
  }

  /**
   * Validate the given data to see if it is compliant and return a compliant object
   */
  clean(input: Partial<TypeAuth>): TypeAuth {
    let session;
    if (typeof input.session === "object") {
      if (
        typeof input.session._id === "string" &&
        typeof input.session.token === "string" &&
        typeof input.session.userId === "string"
      ) {
        // PeyPhone: keep the session even if it was never marked valid (e.g. the
        // tab was reloaded / closed before the first websocket Ready). Dropping it
        // here silently logged people out on the next page load. The session is
        // only removed on explicit logout or an HTTP 401 from the API.
        session = {
          _id: input.session._id,
          token: input.session.token,
          userId: input.session.userId,
          valid: Boolean(input.session.valid),
        };
      }
    }

    return {
      session,
    };
  }

  /**
   * Get current session.
   * @returns Session
   */
  getSession() {
    return this.get().session;
  }

  /**
   * Add a new session to the auth manager.
   * @param session Session
   */
  setSession(session: Session) {
    this.set("session", session);
    void cookieSync("POST", session);
  }

  /**
   * Remove existing session.
   */
  removeSession() {
    this.set("session", undefined!);
    void cookieSync("DELETE");
  }

  /**
   * Mark current session as valid
   */
  markValid() {
    const session = this.get().session;
    if (session && !session.valid) {
      this.set("session", "valid", true);
    }
  }
}
